/**
 * Knowledge Base service — /api/v1/knowledge-bases
 *
 * Verb-in-path like the rest of the platform. Two envelope shapes, as everywhere
 * else here: list endpoints return the paginated envelope unwrapped once, single
 * resources unwrap twice.
 */
import { api } from './api';
import type {
  PipelineSource,
  SourcePreview,
  KbPipelineUsage, KnowledgeBase, KnowledgeBaseDocument,
  PaginatedKbDocumentResponse, PaginatedKnowledgeBaseResponse,
} from '../types';

interface ApiResponse<T> { success: boolean; code: number; message: string; data: T }

export interface KbListParams {
  page?: number; page_size?: number; search?: string; status?: string;
  include_deleted?: boolean; sort_by?: string; sort_order?: 'asc' | 'desc';
  filters?: string; app_id?: string;
  include_global?: boolean;
}

/** One shape for create and update; every field optional so the same form serves both. */
export interface KbUpsertRequest {
  appId?: string;
  kbCode?: string;
  name?: string;
  description?: string | null;
  tags?: string[];
  sourceKind?: string;
  suggestedChunkingPreset?: string | null;
  status?: string;
}

/** The vocabularies the VALIDATOR accepts, served by the backend.
 *  Not a copy in this file: a dropdown that offers a value the save rejects is the
 *  exact failure this endpoint exists to prevent. */
export interface KbOptions {
  sourceKinds: string[];
  statuses: string[];
  chunkingPresets: { code: string; label: string }[];
}

export const knowledgeBaseService = {
  // ---- the corpus ----
  list: async (params?: KbListParams): Promise<PaginatedKnowledgeBaseResponse> =>
    (await api.get<PaginatedKnowledgeBaseResponse>('/api/v1/knowledge-bases/list', { params })).data,

  getById: async (id: string): Promise<KnowledgeBase> =>
    (await api.get<ApiResponse<KnowledgeBase>>(`/api/v1/knowledge-bases/get/${id}`)).data.data,

  create: async (data: KbUpsertRequest): Promise<KnowledgeBase> =>
    (await api.post<ApiResponse<KnowledgeBase>>('/api/v1/knowledge-bases/create', data)).data.data,

  update: async (id: string, data: KbUpsertRequest): Promise<KnowledgeBase> =>
    (await api.put<ApiResponse<KnowledgeBase>>(`/api/v1/knowledge-bases/update/${id}`, data)).data.data,

  softDelete: async (id: string) =>
    (await api.delete<ApiResponse<{ _id: string }>>(`/api/v1/knowledge-bases/delete/${id}`)).data.data,

  restore: async (id: string): Promise<KnowledgeBase> =>
    (await api.post<ApiResponse<KnowledgeBase>>(`/api/v1/knowledge-bases/restore/${id}`)).data.data,

  options: async (): Promise<KbOptions> =>
    (await api.get<ApiResponse<KbOptions>>('/api/v1/knowledge-bases/options')).data.data,

  pipelines: async (id: string): Promise<KbPipelineUsage[]> =>
    (await api.get<ApiResponse<KbPipelineUsage[]>>(`/api/v1/knowledge-bases/${id}/pipelines`)).data.data,

  // ---- documents ----
  documents: async (
    id: string,
    params?: { include_superseded?: boolean; search?: string; page?: number; page_size?: number },
  ): Promise<PaginatedKbDocumentResponse> =>
    (await api.get<PaginatedKbDocumentResponse>(
      `/api/v1/knowledge-bases/${id}/documents`, { params })).data,

  /** Writes version n+1 and closes the predecessor, which is retained. */
  /** Upload a file straight into the corpus — no pipeline needed. Every pipeline
   *  that reads this KB indexes it on its next run. */
  uploadDocument: async (id: string, file: File, meta?: { title?: string; description?: string }) => {
    const form = new FormData();
    form.append('file', file);
    if (meta?.title) form.append('title', meta.title);
    if (meta?.description) form.append('description', meta.description);
    return (await api.post<ApiResponse<PipelineSource>>(`/api/v1/knowledge-bases/${id}/documents/upload`, form,
      { headers: { 'Content-Type': 'multipart/form-data' } })).data.data;
  },

  addDocument: async (id: string, body: { sourceType: 'TEXT' | 'URL'; text?: string; url?: string; title?: string }) =>
    (await api.post<ApiResponse<PipelineSource>>(`/api/v1/knowledge-bases/${id}/documents`, body)).data.data,

  /** One document in full: metadata, provenance, images, text. */
  getDocument: async (id: string, documentId: string): Promise<PipelineSource> =>
    (await api.get<ApiResponse<PipelineSource>>(`/api/v1/knowledge-bases/${id}/documents/${documentId}`)).data.data,

  getDocumentFile: async (id: string, documentId: string): Promise<Blob> =>
    (await api.get(`/api/v1/knowledge-bases/${id}/documents/${documentId}/file`, { responseType: 'blob' })).data,
  getDocumentPreview: async (id: string, documentId: string): Promise<SourcePreview> =>
    (await api.get<ApiResponse<SourcePreview>>(`/api/v1/knowledge-bases/${id}/documents/${documentId}/preview`)).data.data,

  removeDocument: async (id: string, documentId: string) =>
    (await api.delete<ApiResponse<{ _id: string }>>(`/api/v1/knowledge-bases/${id}/documents/${documentId}`)).data.data,

  supersedeDocument: async (
    id: string, documentId: string,
    data: { checksum?: string; title?: string; url?: string; metadata?: Record<string, unknown> },
  ): Promise<KnowledgeBaseDocument> =>
    (await api.post<ApiResponse<KnowledgeBaseDocument>>(
      `/api/v1/knowledge-bases/${id}/documents/${documentId}/supersede`, data)).data.data,
};
